<!--
  Themed pause/delete confirmation -- pause had no confirmation at all
  before this (toggleCampaignStatus fired straight from the menu click),
  and delete used a native window.confirm(), the only place left in
  Campaigns still doing that. Modeled directly on
  Contexts/Dialogs/ContextConfirmDialog.vue's same action-keyed pattern.

  Resume is deliberately NOT one of the actions here -- it's the reversible,
  low-risk side of the same toggle (starts calling again with the exact
  settings it paused with), so it stays a single click like it already was.
-->
<template>
  <Teleport to="body">
    <v-dialog
      :model-value="modelValue"
      max-width="460"
      persistent
      class="caller-dialog-root"
      @update:model-value="$emit('update:modelValue', $event)"
    >
      <div class="caller-dialog" role="dialog" aria-modal="true">
        <header class="caller-dialog__header">
          <div class="caller-dialog__brand">
            <div
              class="caller-dialog__icon"
              :class="{ 'caller-dialog__icon--danger': action === 'delete' }"
              aria-hidden="true"
            >
              <v-icon :icon="icon" size="22" color="white" />
            </div>
            <div>
              <h2 class="caller-dialog__title">{{ title }}</h2>
              <p class="caller-dialog__subtitle">{{ subtitle }}</p>
            </div>
          </div>
          <button
            type="button"
            class="caller-dialog__close"
            aria-label="Close"
            :disabled="saving"
            @click="close"
          >
            <v-icon icon="mdi-close" size="20" />
          </button>
        </header>

        <div class="caller-dialog__body caller-dialog__body--center">
          <p class="caller-dialog__confirm">
            {{ confirmLead }} <strong>"{{ campaignName }}"</strong>?
          </p>
          <p v-if="detail" class="caller-dialog__meta">{{ detail }}</p>
        </div>

        <footer class="caller-dialog__footer">
          <v-btn variant="text" class="caller-dialog__cancel" :disabled="saving" @click="close">
            Cancel
          </v-btn>
          <v-btn
            variant="flat"
            class="caller-dialog__submit"
            :class="{ 'caller-dialog__submit--danger': action === 'delete' }"
            :loading="saving"
            @click="submit"
          >
            {{ confirmLabel }}
          </v-btn>
        </footer>
      </div>
    </v-dialog>
  </Teleport>
</template>

<script setup>
import { computed, ref } from 'vue'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'

defineOptions({ name: 'CampaignConfirmDialog' })

const ACTION_COPY = {
  pause: {
    icon: 'mdi-pause-circle-outline',
    title: 'Pause campaign',
    subtitle: 'Stop calling until you resume it.',
    confirmLead: 'Pause',
    confirmLabel: 'Pause campaign',
    detail: 'No further calls will be placed. Settings, leads and progress are all kept exactly as they are -- resume any time to pick up where it left off.',
    errorFallback: 'Could not pause the campaign.',
    successText: (name) => `"${name}" paused — no further calls will be placed.`,
  },
  delete: {
    icon: 'mdi-trash-can-outline',
    title: 'Delete campaign',
    subtitle: 'This action cannot be undone.',
    confirmLead: 'Delete',
    confirmLabel: 'Delete campaign',
    detail: "This removes the campaign, its lead assignments, its metrics and its history entries. The leads themselves are kept, along with their history on other campaigns.",
    errorFallback: 'Could not delete the campaign.',
    successText: (name) => `"${name}" deleted.`,
  },
}

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  action: {
    type: String,
    default: 'delete',
    validator: (value) => ['pause', 'delete'].includes(value),
  },
  campaign: { type: Object, default: null },
  /** Called with the campaign -- must return the same API response shape isApiSuccess reads. */
  confirmFn: { type: Function, required: true },
})

const emit = defineEmits(['update:modelValue', 'confirmed', 'error'])

const saving = ref(false)

const campaignName = computed(() => props.campaign?.name || 'this campaign')
const copy = computed(() => ACTION_COPY[props.action] || ACTION_COPY.delete)
const icon = computed(() => copy.value.icon)
const title = computed(() => copy.value.title)
const subtitle = computed(() => copy.value.subtitle)
const confirmLead = computed(() => copy.value.confirmLead)
const confirmLabel = computed(() => copy.value.confirmLabel)
const detail = computed(() => copy.value.detail)

function close() {
  if (saving.value) return
  emit('update:modelValue', false)
}

async function submit() {
  saving.value = true
  try {
    const response = await props.confirmFn(props.campaign)
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, copy.value.errorFallback))
      return
    }
    emit('confirmed', copy.value.successText(campaignName.value))
    emit('update:modelValue', false)
  } catch (error) {
    emit('error', getApiMessage(error, copy.value.errorFallback))
  } finally {
    saving.value = false
  }
}
</script>
